import { Button, notify } from '@affine/component';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { ServerService } from '@affine/core/modules/cloud';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { I18n, useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';

import { UserMcpCredentialsPanel } from '../../../account-setting/user-mcp-credentials-panel';
import { IntegrationSettingHeader } from '../setting';
import MCPIcon from './MCP.inline.svg';
import * as styles from './setting-panel.css';

const capabilityKeys = [
  'browse',
  'search',
  'write',
  'manage',
  'upload',
  'table',
] as const;

const agentGuideStepKeys = ['create', 'configure', 'discover'] as const;

export const McpServerSettingPanel = () => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const serverService = useService(ServerService);
  const workspaceId = workspace.id;
  const endpoint = `${serverService.server.baseUrl}/api/workspaces/${encodeURIComponent(workspaceId)}/mcp`;
  const staticResourceUrl = (path: string) => {
    const url = new URL(serverService.server.baseUrl);
    const basePath = url.pathname.replace(/\/+$/, '');
    url.pathname = `${basePath}/${path.replace(/^\/+/, '')}`;
    url.search = '';
    url.hash = '';
    return url.toString();
  };
  const language = I18n.language.toLowerCase();
  const resourceSuffix = language.startsWith('en')
    ? '.en'
    : language === 'zh-hant' ||
        language.startsWith('zh-tw') ||
        language.startsWith('zh-hk')
      ? '.zh-Hant'
      : '';
  const skillUrl = staticResourceUrl(
    `mcp/cangjie-affine/SKILL${resourceSuffix}.md`
  );

  const copyEndpoint = useAsyncCallback(async () => {
    await navigator.clipboard.writeText(endpoint);
    notify.success({ title: t['Copied to clipboard']() });
  }, [endpoint, t]);

  const copyAgentPrompt = useAsyncCallback(async () => {
    const agentPrompt = [
      t['com.affine.integration.mcp-server.agent-guide.prompt.intro'](),
      t['com.affine.integration.mcp-server.agent-guide.prompt.endpoint']({
        endpoint,
      }),
      t['com.affine.integration.mcp-server.agent-guide.prompt.rules'](),
      `Skill: ${skillUrl}`,
    ].join('\n\n');

    await navigator.clipboard.writeText(agentPrompt);
    notify.success({ title: t['Copied to clipboard']() });
  }, [endpoint, skillUrl, t]);

  return (
    <div className={styles.stack}>
      <IntegrationSettingHeader
        icon={<img src={MCPIcon} />}
        name={t['com.affine.integration.mcp-server.name']()}
        desc={t['com.affine.integration.mcp-server.desc']()}
      />

      <section className={styles.panel}>
        <div className={styles.endpointBlock}>
          <div className={styles.endpointHeader}>
            <div>
              <div className={styles.label}>
                {t['com.affine.integration.mcp-server.endpoint.title']()}
              </div>
              <div className={styles.description}>
                {t['com.affine.integration.mcp-server.endpoint.description']()}
              </div>
            </div>
            <Button onClick={copyEndpoint}>
              {t['com.affine.integration.mcp-server.action.copy-endpoint']()}
            </Button>
          </div>
          <code className={styles.endpoint}>{endpoint}</code>
        </div>
      </section>
      <UserMcpCredentialsPanel />

      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <div className={styles.title}>
              {t['com.affine.integration.mcp-server.capabilities.title']()}
            </div>
            <div className={styles.description}>
              {t[
                'com.affine.integration.mcp-server.capabilities.description'
              ]()}
            </div>
          </div>
        </div>
        <div className={styles.capabilities}>
          {capabilityKeys.map(key => (
            <div className={styles.capability} key={key}>
              {t[`com.affine.integration.mcp-server.capabilities.${key}`]()}
            </div>
          ))}
        </div>
      </section>

      <section className={styles.panel} data-testid="mcp-agent-guide">
        <div className={styles.panelHeader}>
          <div>
            <div className={styles.title}>
              {t['com.affine.integration.mcp-server.agent-guide.title']()}
            </div>
            <div className={styles.description}>
              {t['com.affine.integration.mcp-server.agent-guide.description']()}
            </div>
          </div>
        </div>
        <ol className={styles.agentGuideSteps}>
          {agentGuideStepKeys.map((key, index) => (
            <li className={styles.agentGuideStep} key={key}>
              <span className={styles.agentGuideStepNumber}>{index + 1}</span>
              <div>
                <div className={styles.label}>
                  {t[
                    `com.affine.integration.mcp-server.agent-guide.${key}.title`
                  ]()}
                </div>
                <div className={styles.description}>
                  {t[
                    `com.affine.integration.mcp-server.agent-guide.${key}.description`
                  ]()}
                </div>
              </div>
            </li>
          ))}
        </ol>
        <div className={styles.agentSkill}>
          <div>
            <div className={styles.label}>
              {t['com.affine.integration.mcp-server.agent-skill.title']()}
            </div>
            <div className={styles.description}>
              {t['com.affine.integration.mcp-server.agent-skill.description']()}
            </div>
          </div>
          <div className={styles.agentSkillActions}>
            <a
              className={styles.skillLink}
              href={skillUrl}
              download={`cangjie-affine-SKILL${resourceSuffix}.md`}
            >
              {t.t('com.affine.integration.mcp-server.action.download-skill')}
            </a>
            <Button onClick={copyAgentPrompt}>
              {t[
                'com.affine.integration.mcp-server.action.copy-agent-prompt'
              ]()}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
